import $ = require('jquery');
import websiteData = require('diy/util/websiteData');
let semanticDataRefresher = require<any>('diy/semanticdata/refresher/refresher');
let semanticDataApi = require<any>('diy/semanticdata/api/api');

declare let jimdoData: any;

interface AjaxCallSettings {
    url: string;
    parameters?: any;
}

interface VCardDataAccessConfig {
    vCardPayload: {
        load: AjaxCallSettings,
        upgrade: AjaxCallSettings,
        save: AjaxCallSettings,
        applyLogo: AjaxCallSettings,
        saveResource: AjaxCallSettings,
        version: number
    };
    semanticData: {
        load: AjaxCallSettings,
        save: AjaxCallSettings
    };
}

export default class VCardDataModel {
    protected config: VCardDataAccessConfig = {
        vCardPayload: {
            load: {
                url: '/rest/websites/' + websiteData.getId() + '/vcard',
                parameters: {
                    contentType: 'application/vnd.oneandone.diy.website.vcard+json'
                }
            },
            save: {
                url: '/rest/websites/' + websiteData.getId() + '/vcard',
                parameters: {
                    contentType: 'application/vnd.oneandone.diy.website.vcard+json',
                    dataType: 'json'
                }
            },
            applyLogo: {
                url: '/rest/websites/' + websiteData.getId() + '/vcard',
                parameters: {
                    headers: {Accept: 'application/vnd.oneandone.diy.website.logo+json'},
                    contentType: 'application/vnd.oneandone.diy.website.logo+json',
                    dataType: 'json'
                }
            },
            saveResource: {
                url: '/rest/websites/' + websiteData.getId() + '/storage-files',
                parameters: {
                    contentType: 'application/vnd.oneandone.diy.website.storage.file+json',
                    dataType: 'json'
                }
            },
            upgrade: {
                url: '/rest/websites/' + websiteData.getId() + '/vcard',
                parameters: {
                    'contentType': 'application/vnd.oneandone.diy.website.vcard+json',
                    data: {
                        upgradeData: true
                    }
                }
            },
            version: 0
        },
        semanticData: {
            load: {
                url: '/rest/websites/' + websiteData.getId() + '/semantic-data',
                parameters: {
                    'contentType': 'application/vnd.oneandone.diy.website.semantic-data+json'
                }
            },
            save: {
                url: '/rest/websites/' + websiteData.getId() + '/semantic-data',
                parameters: {
                    'contentType': 'application/vnd.oneandone.diy.website.semantic-data+json'
                }
            }
        }
    };

    constructor(version: number) {
        this.config.vCardPayload.version = version;
    }

    /**
     * @return array of promises
     */
    load(): Promise<VCardClientData> {
        return Promise.all([
            this.getVCardPayload(),
            this.getSemanticData()
        ]).then(([vCardPayload, semanticData]) => {
            return {
                payload: vCardPayload,
                semanticData: semanticData
            };
        });
    }

    save(data: VCardClientData) {
        let logoImageId = data.logoImageId;

        let saveData = () => {
            return Promise.all([
                this.saveSemanticData(data.semanticData),
                this.saveDataByConfig(this.config.vCardPayload.save, data.payload)
            ].concat(logoImageId ? [
                this.saveDataByConfig<any>(this.config.vCardPayload.applyLogo, {
                    imageId: logoImageId
                }).then(logoData => {
                    if (
                        typeof diy !== 'undefined' &&
                        typeof diy.module !== 'undefined' &&
                        typeof diy.module.logo !== 'undefined' &&
                        typeof diy.module.logo.logo !== 'undefined' &&
                        typeof diy.module.logo.logo.logoExists === 'function' &&
                        typeof diy.module.logo.logo.loadData === 'function' &&
                        diy.module.logo.logo.logoExists()
                    ) {
                        diy.module.logo.logo.loadData(logoData);
                    } else {
                        if (
                            typeof CC !== 'undefined' &&
                            typeof CC.EmotionHeader !== 'undefined' &&
                            typeof CC.EmotionHeader.reload === 'function'
                        ) {
                            CC.EmotionHeader.reload();
                        }
                    }
                })
            ] : []));
        };

        if (data["resources"] && data["resources"].length) {
            return Promise.all(
                data["resources"].map((resourceData) => {
                    return this.saveResource(this.config.vCardPayload.saveResource, resourceData);
                })
            ).then(() => {
                return saveData();
            });
        } else {
            return saveData();
        }
    }

    // destroy siteadmin cache window for settings and updates the in page modules with the updated data
    refreshSemanticData(data: VCardSemanticData): void {
        try {
            if (
                diy && diy.editor && diy.editor.siteadmin && diy.editor.siteadmin.api
            ) {
                diy.editor.siteadmin.api.close();
                diy.editor.siteadmin.api.destroyController('cc-sa-item-settings');
                semanticDataApi.deleteCache();
                semanticDataRefresher.update(data);
            }
        } catch (error) {
            // ignore error due to the fact that save action can also be triggered when there is no site admin window
        }
    }

    saveSemanticData(data: VCardSemanticData): Promise<SemanticDataResponseValues> {
        let config = this.config.semanticData.save;
        const refreshSemanticData = this.refreshSemanticData;
        let saveData = () => {
            return new Promise(function (resolve, reject) {

                let xhr = new XMLHttpRequest();
                /* register successful event */
                xhr.addEventListener("load", function (e) {
                    let target = e.target as XMLHttpRequest;
                    if (target.status >= 200 && target.status < 300) {
                        refreshSemanticData(data);
                        resolve([]);
                    } else if (target.status === 422) {
                        resolve(JSON.parse(target.response));
                    } else {
                        reject({ code: target.status });
                    }
                });
                /* register error event */
                xhr.addEventListener("error", function (e) {
                    reject(e);
                });

                xhr.open('POST', config.url, true);
                xhr.setRequestHeader('X-DiyCSRFToken', jimdoData.cstok);
                xhr.setRequestHeader('Content-Type', config.parameters['contentType']);
                if (config.parameters['accepts']) {
                    xhr.setRequestHeader('Accept', config.parameters['accepts']);
                }
                xhr.send(JSON.stringify(data));
            });
        };

        return saveData();
    }

    protected getVCardPayload() {
        return this.getDataByConfig(this.config.vCardPayload.load)
            .then((payload: VCardPayload) => {
                if (payload.version !== this.config.vCardPayload.version) {
                    return this.getDataByConfig(this.config.vCardPayload.upgrade);
                }

                return payload;
            });
    }

    protected getSemanticData() {
        return this.getDataByConfig(this.config.semanticData.load);
    }

    protected saveResource(config: AjaxCallSettings, data: any) {
        return new Promise(function (resolve, reject) {

            let xhr = new XMLHttpRequest();
            /* register successful event */
            xhr.addEventListener("load", function (e) {
                let target = e.target as XMLHttpRequest;
                if (target.status >= 200 && target.status < 300) {
                    resolve();
                } else { // if we received an error http code
                    reject({ code: target.status });
                }
            });
            /* register error event */
            xhr.addEventListener("error", function (e) {
                reject("error");
            });

            let formData = new FormData();

            formData.append("data", JSON.stringify({
                "publicName": data.filename,
                "folderId": null,
                "uuid": data.id
            }));

            formData.append("file", data.file);

            xhr.open('POST', config.url, true);
            xhr.setRequestHeader('X-DiyCSRFToken', jimdoData.cstok);
            xhr.send(formData);
        });
    }

    protected saveDataByConfig<TResponse>(config: AjaxCallSettings, data: any) {
        let settings: any = $.extend(true,
            {
                url: config.url,
                type: 'POST',
                data: JSON.stringify(data)
            },
            config.parameters
        );

        return Promise.resolve<TResponse>($.ajax(settings));
    }

    protected getDataByConfig(config: AjaxCallSettings): Promise<VCardPayload> {
        return Promise.resolve($.ajax(
            $.extend(
                {
                    url: config.url,
                    type: 'GET'
                },
                config.parameters
            )
        ));
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/vcard/model.ts
 **/